/**
 * Algolia resource search (Alpine component) for the /search-demo test page.
 *
 * Queries Algolia's REST API directly with the search-only key; no Algolia
 * search library. Markup lives in templates/_search-demo/index.twig.
 *
 * Click, view and filter-selection events go to Algolia Insights through search-insights, which
 * keeps an anonymous user token in the _ALGOLIA cookie. Each hit carries the
 * queryID and absolute position of the response it came from, since "Load
 * more" mixes hits from several responses.
 *
 * Resource Type filter: each new search sends two queries in one multi-query
 * request — the results (filtered by the selected type) and the type counts
 * (unfiltered, so other options show what switching would return, matching the
 * live listing). The counts query has analytics off so a search isn't counted twice.
 *
 * Expects data attributes on the x-data element:
 *   data-app-id, data-search-key, data-index
 */
import aa from 'search-insights';

const HITS_PER_PAGE = 9;
const TYPE_FACET = 'resource_type_slug';

export default () => ({
	query: '',
	type: '',
	typeCounts: {},
	filtersOpen: false,
	hits: [],
	total: null,
	page: 0,
	pages: 0,
	loading: false,
	error: false,
	requestId: 0,

	get hasMore() {
		return !this.error && this.page + 1 < this.pages;
	},

	get hasActiveFilters() {
		return this.query.trim() !== '' || this.type !== '';
	},

	typeCount(slug) {
		return this.typeCounts[slug] ?? 0;
	},

	// Zero-count options are disabled unless selected (same rule as the live listing)
	typeDisabled(slug) {
		return this.typeCount(slug) === 0 && this.type !== slug;
	},

	clearType() {
		this.type = '';
	},

	clearKeyword() {
		this.query = '';
	},

	init() {
		const { appId, searchKey, index } = this.$el.dataset;
		this.index = index;
		aa('init', { appId, apiKey: searchKey, useCookie: true });

		this.endpoint = `https://${appId}-dsn.algolia.net/1/indexes/*/queries`;
		this.headers = {
			'X-Algolia-Application-Id': appId,
			'X-Algolia-API-Key': searchKey,
			'Content-Type': 'application/json',
			// Ties searches to the same user as the Insights events
			'X-Algolia-UserToken': String(aa('getUserToken')),
		};

		const params = new URLSearchParams(window.location.search);
		this.query = params.get('q') ?? '';
		this.type = params.get('type') ?? '';
		const refresh = () => {
			this.syncUrl();
			this.search();
		};
		this.$watch('query', refresh);
		// Registered after reading ?type=, so loading a shared URL doesn't count as a selection
		this.$watch('type', (type) => {
			if (type) this.trackFilter(type);
			refresh();
		});
		this.search();
	},

	search() {
		return this.fetchPage(0);
	},

	loadMore() {
		return this.fetchPage(this.page + 1);
	},

	async fetchPage(page) {
		// Ignore responses that arrive after a newer request started (fast typing)
		const requestId = ++this.requestId;
		this.loading = true;
		this.error = false;

		const query = this.query.trim();
		const requests = [
			{
				indexName: this.index,
				query,
				page,
				hitsPerPage: HITS_PER_PAGE,
				clickAnalytics: true,
				...(this.type && { facetFilters: [[`${TYPE_FACET}:${this.type}`]] }),
			},
		];
		// Type counts only change with the keyword, so Load more skips them
		if (page === 0) {
			requests.push({
				indexName: this.index,
				query,
				hitsPerPage: 0,
				facets: [TYPE_FACET],
				analytics: false,
				clickAnalytics: false,
			});
		}

		try {
			const response = await fetch(this.endpoint, {
				method: 'POST',
				headers: this.headers,
				body: JSON.stringify({ requests }),
			});
			if (!response.ok) throw new Error(`Algolia responded ${response.status}`);
			const [data, counts] = (await response.json()).results;
			if (requestId !== this.requestId) return;

			if (counts) this.typeCounts = counts.facets?.[TYPE_FACET] ?? {};
			const hits = data.hits.map((hit, i) => ({
				...hit,
				__queryID: data.queryID,
				__position: data.page * data.hitsPerPage + i + 1,
			}));
			this.hits = page === 0 ? hits : [...this.hits, ...hits];
			this.total = data.nbHits;
			this.page = data.page;
			this.pages = data.nbPages;
			this.trackView(hits);
		} catch (e) {
			if (requestId !== this.requestId) return;
			console.error(e);
			this.error = true;
			if (page === 0) {
				this.hits = [];
				this.total = null;
			}
		} finally {
			if (requestId === this.requestId) this.loading = false;
		}
	},

	trackView(hits) {
		const objectIDs = hits.map((hit) => hit.objectID);
		if (!objectIDs.length) return;
		aa('viewedObjectIDs', { index: this.index, eventName: 'Resources Viewed', objectIDs });
	},

	// Filter selections feed Algolia Personalization (which types each visitor prefers)
	trackFilter(type) {
		aa('clickedFilters', {
			index: this.index,
			eventName: 'Resource Type Selected',
			filters: [`${TYPE_FACET}:${type}`],
		});
	},

	trackClick(hit) {
		if (!hit.__queryID) return;
		aa('clickedObjectIDsAfterSearch', {
			index: this.index,
			eventName: 'Resource Clicked',
			queryID: hit.__queryID,
			objectIDs: [hit.objectID],
			positions: [hit.__position],
		});
	},

	// Keep ?q= and ?type= in sync so a search can be shared or reloaded
	syncUrl() {
		const url = new URL(window.location.href);
		const q = this.query.trim();
		if (q) url.searchParams.set('q', q);
		else url.searchParams.delete('q');
		if (this.type) url.searchParams.set('type', this.type);
		else url.searchParams.delete('type');
		window.history.replaceState({}, '', url);
	},
});
